import { FC } from 'react';
import {
  ProgramAssessmentDataFragment,
  CompletedProgramAssessmentsDataQuery,
} from '../../../../generated/graphql';
import ModalDialog from '../../ModalDialog';
import ResponsesSection from '../../Assessments/ResponsesSection';

type FormResponsesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  programAssessment?:
    | ProgramAssessmentDataFragment
    | CompletedProgramAssessmentsDataQuery['completedProgramAssessmentsByAssessment'][number];
  assessmentName?: string;
  patientName?: string;
};

const FormResponsesModal: FC<FormResponsesModalProps> = ({
  isOpen,
  onClose,
  programAssessment,
  assessmentName,
  patientName,
}) => {
  const modalTitle = assessmentName || 'Form Responses';
  const hasResponses = Boolean(programAssessment?.answers?.length);

  const isPractitionerForm =
    programAssessment?.assessment?.completableByProviderOnly === true;
  const pa = programAssessment as
    | (typeof programAssessment & {
        completedByType?: string | null;
        completedByProviderUser?: { name?: string | null } | null;
        programAssessmentSignatureUsers?: Array<{
          signedAt?: unknown;
          providerUser?: { name?: string | null };
        }>;
      })
    | undefined;
  const signatureProviderName = pa?.programAssessmentSignatureUsers?.find(
    (u) => u.signedAt,
  )?.providerUser?.name;
  const completedByProviderName =
    signatureProviderName ?? pa?.completedByProviderUser?.name;
  const completedByClient =
    isPractitionerForm && pa?.completedByType === 'patient';
  const respondentName = isPractitionerForm
    ? completedByProviderName ?? (completedByClient ? 'Client' : null)
    : patientName;

  return (
    <ModalDialog
      isOpen={isOpen}
      setClosed={onClose}
      title={modalTitle}
      width="large"
      overflow="hidden"
      hideFooter
      bodyClassName="pb-0 overflow-y-auto max-h-[calc(90vh-120px)]"
      titleClassName="text-big-label font-serif font-normal pt-3"
      headerClassName="border-none"
      className="max-h-[90vh]"
    >
      <div className="space-y-6">
        {/* Respondent context: patient name or practitioner who completed */}
        {respondentName && (
          <div className="text-base text-neutral-125">
            Responses from {respondentName}
          </div>
        )}

        {/* Form responses with larger font sizes */}
        <div className="[&_.text-caption]:!text-lg [&_.text-small-label]:!text-base [&_.editor]:!text-lg">
          <ResponsesSection
            title=""
            programAssessment={programAssessment}
            questionFrame={
              programAssessment?.assessment?.questionFrame ?? undefined
            }
          />
        </div>

        {/* Additional context if no responses yet */}
        {!hasResponses && (
          <div className="py-8 text-center text-neutral-125">
            <div className="text-base">
              Waiting for{' '}
              {respondentName
                ? `${respondentName} to complete`
                : 'completion of'}{' '}
              this form
            </div>
          </div>
        )}
      </div>
    </ModalDialog>
  );
};

export default FormResponsesModal;
